/**
 * TanStack Query hooks for the reference quantities v2 API.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchReferenceQuantities,
  fetchRefQuantityHistory,
  upsertReferenceQuantity,
  importReferenceQuantities,
  transferOwnership,
  deleteReferenceQuantity,
  approveParameterChange,
  rejectParameterChange,
  type ReferenceQuantity,
  type RefQuantityUpsert,
  type RefQuantityImportItem,
  type RefQuantityHistoryEntry,
} from "../api/referenceQuantities";

export function useReferenceQuantities(
  projectId: string,
  branch = "main",
  configurationId?: string,
) {
  return useQuery<ReferenceQuantity[]>({
    queryKey: ["reference-quantities", projectId, branch, configurationId],
    queryFn: () => fetchReferenceQuantities(projectId, branch, configurationId),
    enabled: !!projectId,
  });
}

export function useUpsertReferenceQuantity(projectId: string, branch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: RefQuantityUpsert) => upsertReferenceQuantity(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["reference-quantities", projectId, branch] });
      qc.invalidateQueries({ queryKey: ["run-table", projectId, branch] });
    },
  });
}

export function useImportReferenceQuantities(projectId: string, branch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      quantities,
      configurationId,
    }: {
      quantities: RefQuantityImportItem[];
      configurationId?: string;
    }) => importReferenceQuantities(projectId, branch, quantities, configurationId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["reference-quantities", projectId, branch] }),
  });
}

export function useDeleteReferenceQuantity(projectId: string, branch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeId, reason }: { nodeId: string; reason: string }) =>
      deleteReferenceQuantity(nodeId, reason),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["reference-quantities", projectId, branch] }),
  });
}

export function useApproveParameter(projectId: string, branch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeId, comment }: { nodeId: string; comment?: string }) =>
      approveParameterChange(nodeId, comment),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["reference-quantities", projectId, branch] }),
  });
}

export function useRejectParameter(projectId: string, branch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeId, comment }: { nodeId: string; comment?: string }) =>
      rejectParameterChange(nodeId, comment),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["reference-quantities", projectId, branch] }),
  });
}

export function useTransferOwnership(projectId: string, branch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ nodeId, ownedBy }: { nodeId: string; ownedBy: string }) =>
      transferOwnership(nodeId, ownedBy),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["reference-quantities", projectId, branch] }),
  });
}

export function useRefQuantityHistory(
  nodeId: string | undefined,
  projectId: string,
  branch = "main",
) {
  return useQuery<RefQuantityHistoryEntry[]>({
    queryKey: ["ref-quantity-history", nodeId, projectId, branch],
    queryFn: () => fetchRefQuantityHistory(nodeId!, projectId, branch),
    enabled: !!nodeId && !!projectId,
  });
}
